import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { ResultSegment } from "../../types";
import { formatPercentage, getScoreColor, getScoreLabel } from "../../utils/data-mapper";

const styles = StyleSheet.create({
  container: {
    marginVertical: 15
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#374151",
    marginBottom: 10
  },
  table: {
    width: "100%"
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 8,
    paddingHorizontal: 10
  },
  dataRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    paddingVertical: 8,
    paddingHorizontal: 10
  },
  alternateRow: {
    backgroundColor: "#fafafa"
  },
  dimensionCell: {
    flex: 3,
    paddingRight: 10
  },
  scoreCell: {
    flex: 1,
    alignItems: "center"
  },
  autoAvaliacaoCell: {
    flex: 1,
    alignItems: "center"
  },
  statusCell: {
    flex: 1.5,
    alignItems: "center"
  },
  headerText: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#374151"
  },
  dimensionText: {
    fontSize: 9,
    color: "#1f2937"
  },
  scoreText: {
    fontSize: 9,
    fontWeight: "bold"
  },
  statusBadge: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4
  },
  statusText: {
    fontSize: 8,
    color: "#ffffff",
    fontWeight: "bold"
  },
  summaryRow: {
    flexDirection: "row",
    backgroundColor: "#1e3a5f",
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginTop: 2
  },
  summaryText: {
    fontSize: 10,
    color: "#ffffff",
    fontWeight: "bold"
  }
});

interface DataTableProperties {
  resultados: ResultSegment[];
  showAutoAvaliacao?: boolean;
  title?: string;
}

/**
 * DataTable component for displaying dimension scores in a tabular format
 * @param root0
 * @param root0.resultados
 * @param root0.showAutoAvaliacao
 * @param root0.title
 */
export function DataTable({
  resultados,
  showAutoAvaliacao = true,
  title = "Resultados por Dimensão"
}: DataTableProperties) {
  const hasAutoAvaliacao = showAutoAvaliacao && resultados.some((r) => r.autoAvaliacao !== undefined);

  // Calculate average
  let totalScore = 0;
  for (const r of resultados) {
    totalScore += r.value;
  }
  const averageScore = resultados.length > 0 ? totalScore / resultados.length : 0;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{title}</Text>

      <View style={styles.table}>
        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.dimensionCell}>
            <Text style={styles.headerText}>Dimensão</Text>
          </View>
          <View style={styles.scoreCell}>
            <Text style={styles.headerText}>Pontuação</Text>
          </View>
          {hasAutoAvaliacao && (
            <View style={styles.autoAvaliacaoCell}>
              <Text style={styles.headerText}>Autoavaliação</Text>
            </View>
          )}
          <View style={styles.statusCell}>
            <Text style={styles.headerText}>Status</Text>
          </View>
        </View>

        {/* Data Rows */}
        {resultados.map((resultado, index) => {
          const scoreColor = getScoreColor(resultado.value);
          const scoreLabel = getScoreLabel(resultado.value);
          const isAlternate = index % 2 === 1;
          const rowStyle = isAlternate ? [styles.dataRow, styles.alternateRow] : [styles.dataRow];

          return (
            <View key={resultado.segment} style={rowStyle}>
              <View style={styles.dimensionCell}>
                <Text style={styles.dimensionText}>{resultado.segment}</Text>
              </View>
              <View style={styles.scoreCell}>
                <Text style={[styles.scoreText, { color: scoreColor }]}>{formatPercentage(resultado.value)}</Text>
              </View>
              {hasAutoAvaliacao && (
                <View style={styles.autoAvaliacaoCell}>
                  <Text style={styles.scoreText}>
                    {resultado.autoAvaliacao === undefined ? "-" : formatPercentage(resultado.autoAvaliacao)}
                  </Text>
                </View>
              )}
              <View style={styles.statusCell}>
                <View style={[styles.statusBadge, { backgroundColor: scoreColor }]}>
                  <Text style={styles.statusText}>{scoreLabel}</Text>
                </View>
              </View>
            </View>
          );
        })}

        {/* Summary Row */}
        <View style={styles.summaryRow}>
          <View style={styles.dimensionCell}>
            <Text style={styles.summaryText}>MÉDIA GERAL</Text>
          </View>
          <View style={styles.scoreCell}>
            <Text style={styles.summaryText}>{formatPercentage(averageScore)}</Text>
          </View>
          {hasAutoAvaliacao && (
            <View style={styles.autoAvaliacaoCell}>
              <Text style={styles.summaryText}>-</Text>
            </View>
          )}
          <View style={styles.statusCell}>
            <Text style={styles.summaryText}>{getScoreLabel(averageScore)}</Text>
          </View>
        </View>
      </View>
    </View>
  );
}
